import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../Layout";
import staffApi from "../../api/user/staff.api";
import { toast } from "react-hot-toast";

import StaffFilter from "./StaffFilter";
import RosterHeader from "./RosterHeader";
import RosterTable from "./RosterTable";
import RosterLegend from "./RosterLegend";
import { getMonthDates } from "./rosterHelper";
import Loading from "../../Util/Loading";
import rosterApi from "../../api/roster/RosterApi";
import clsx from "clsx";
import ProcessingBar2 from "../../components/ProcessingBar/ProcessingBar2";

const RosterPage = () => {
  const [staffs, setStaffs] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("all");
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [colWidths, setColWidths] = useState({});
  const [loading, setLoading] = useState(false);
  const [closedDates, setClosedDates] = useState([]);

  const [originalOverrides, setOriginalOverrides] = useState({});

  // get closed date
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { response } = await rosterApi.getClosedDates();
        setClosedDates(response?.data?.closedDates || []);
      } catch {
        toast.error("Failed to load closed dates");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ✅ SINGLE SOURCE OF TRUTH
  const [draftRoster, setDraftRoster] = useState({});

  const dates = useMemo(() => getMonthDates(year, month), [year, month]);

  const visibleStaffs =
    selectedStaffId === "all"
      ? staffs
      : staffs.filter((s) => s._id === selectedStaffId);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { response } = await staffApi.getAllStaffInfo();
        setStaffs(response?.data?.data || []);
      } catch {
        toast.error("Failed to load staff");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const buildRosterPayload = (draftRoster) => {
    return {
      items: Object.entries(draftRoster).map(([key, value]) => {
        const [staffId, date] = key.split("__"); // ✅ SAFE

        return {
          staffId,
          date, // "2026-01-16"
          scope: "staff",
          note: "Roster override",
          ...value,
        };
      }),
    };
  };

  // 🔁 Convert backend overrides → draftRoster map
  const overridesToDraft = (overrides) => {
    const map = {};

    overrides.forEach((o) => {
      const d = new Date(o.date); // UTC → browser local (NZ)
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");

      const dateKey = `${y}-${m}-${day}`; // ✅ LOCAL DAY
      const key = `${o.staffId}__${dateKey}`;

      map[key] = {
        dayOff: !!o.dayOff,
        startHour: o.startHour ?? null,
        endHour: o.endHour ?? null,
      };
    });

    return map;
  };

  const loadOverrides = async () => {
    try {
      const { response } = await rosterApi.getStaffRosterOverrides({
        year,
        month: month + 1,
      });

      console.log({ response });

      const overrides = response?.data?.overrides || [];
      const draft = overridesToDraft(overrides);

      setDraftRoster(draft);
      setOriginalOverrides(draft); // ✅ SNAPSHOT
    } catch {
      toast.error("Failed to load roster overrides");
    }
  };

  useEffect(() => {
    loadOverrides();
  }, [year, month]);

  // console.log({ draftRoster });

  const handleSaveRoster = async () => {
    if (loading) return;

    if (Object.keys(draftRoster).length === 0) {
      toast("No changes to save");
      return;
    }

    const payload = buildRosterPayload(draftRoster);
    console.log("✅ SAVE ROSTER PAYLOAD:", payload);

    try {
      setLoading(true);

      const response = await rosterApi.saveStaffRoster(payload);

      console.log("✅ SAVE ROSTER RESPONSE:", response);

      toast.success(response?.message || "Roster saved successfully");

      // setDraftRoster({});

      // ✅ REFRESH FROM BACKEND (important)
      await loadOverrides();
    } catch (err) {
      console.error("❌ Save roster error:", err);
      toast.error(err?.response?.data?.message || "Failed to save roster");
      throw err; // 👈 optional: rethrow for upstream handling
    } finally {
      setLoading(false);
    }
  };

  const hasRosterChanges = useMemo(() => {
    const keys = new Set([
      ...Object.keys(draftRoster),
      ...Object.keys(originalOverrides),
    ]);

    for (const key of keys) {
      const current = draftRoster[key];
      const original = originalOverrides[key];

      // If one exists and the other doesn't → changed
      if (!current || !original) return true;

      // Deep compare values
      if (
        current.dayOff !== original.dayOff ||
        current.startHour !== original.startHour ||
        current.endHour !== original.endHour
      ) {
        return true;
      }
    }

    return false;
  }, [draftRoster, originalOverrides]);

  const [readyToPublish, setReadyToPublish] = useState(false);
  useEffect(() => {
    if (hasRosterChanges) {
      setReadyToPublish(false);

      const timer = setTimeout(() => {
        setReadyToPublish(true);
      }, 2000); // ⏱ 1 second delay

      return () => clearTimeout(timer);
    } else {
      setReadyToPublish(false);
    }
  }, [hasRosterChanges]);

  return (
    <Layout>
      {loading ? (
        <Loading />
      ) : (
        <div className="min-h-screen text-textgray p-6">
          <div className="flex justify-between mb-6 min-h-[70px] ">
            <h1 className="text-2xl font-semibold text-yellowlight">Roster</h1>
            <div className="flex flex-col items-end ">
              {/* <button
                onClick={handleSaveRoster}
                disabled={!hasRosterChanges || loading}
                className={`
    px-4 py-2 rounded shadow transition
    ${
      hasRosterChanges && !loading
        ? "bg-yellowlight text-yellowdark hover:text-white hover:bg-yellowdark shadow-yellowdark cursor-pointer"
        : "bg-gray-300 text-textGray cursor-not-allowed shadow-none"
    }
  `}
              >
                {loading ? "Publishing..." : "Publish"}
              </button> */}
              <button
                onClick={handleSaveRoster}
                disabled={!readyToPublish || loading}
                className={clsx(
                  "px-4 py-2 rounded shadow transition min-w-[120px] min-h-[40px]",
                  "flex items-center justify-center", // 👈 ADD THIS
                  readyToPublish && !loading
                    ? "bg-yellowlight text-yellowdark hover:text-white hover:bg-yellowdark shadow-yellowdark cursor-pointer"
                    : "bg-gray-300 text-textGray cursor-not-allowed shadow-none"
                )}
              >
                {loading ? (
                  "Publishing..."
                ) : hasRosterChanges && !readyToPublish ? (
                  <ProcessingBar2 />
                ) : (
                  "Publish"
                )}
              </button>
            </div>

            {/* {hasRosterChanges && !loading && readyToPublish && (
              <p className="mt-2 text-yellowlight text-sm italic">
                Click to publish
              </p>
            )}

            {hasRosterChanges && !readyToPublish && (
              <p className="mt-2 text-textGray text-sm italic">
                Preparing changes…
              </p>
            )} */}
          </div>

          <div className="flex flex-wrap gap-4 mb-4 items-center">
            <StaffFilter
              staffs={staffs}
              value={selectedStaffId}
              onChange={setSelectedStaffId}
            />
            <RosterHeader
              month={month}
              year={year}
              setMonth={setMonth}
              setYear={setYear}
            />
          </div>

          <RosterTable
            staffs={visibleStaffs}
            dates={dates}
            colWidths={colWidths}
            setColWidths={setColWidths}
            draftRoster={draftRoster}
            setDraftRoster={setDraftRoster}
            closedDates={closedDates} // ✅
            originalOverrides={originalOverrides} // ✅
            onResetStaffSuccess={loadOverrides} // ✅ ADD THIS
          />

          <RosterLegend />
        </div>
      )}
    </Layout>
  );
};

export default RosterPage;
